import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  PrinterIcon,
  CheckBadgeIcon,
  ExclamationCircleIcon,
  DocumentTextIcon,
  ArrowDownTrayIcon,
  LockClosedIcon
} from '@heroicons/react/24/outline';
import { crmAPI, API_BASE_URL } from '../services/api';
import BrandLogo from '../components/common/BrandLogo';
import { resolveImageUrl } from '../utils/image';
import toast from 'react-hot-toast';

const InvoicePage: React.FC = () => {
    const { invoiceId } = useParams<{ invoiceId: string }>();
    const [searchParams] = useSearchParams();
    const [invoice, setInvoice] = useState<any>(null);
    const [project, setProject] = useState<any>(null);
    const [user, setUser] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [password, setPassword] = useState('');
    const [passInput, setPassInput] = useState('');
    const [authenticated, setAuthenticated] = useState(false);
    const [logoError, setLogoError] = useState(false);
    
    // Rejection state
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectReason, setRejectReason] = useState('');

    const fetchInvoice = async (pwd: string = password) => {
        try {
            setLoading(true);
            const res = await crmAPI.getPublicInvoice(`${invoiceId}?password=${pwd}`);
            setInvoice(res.data.data.invoice);
            setProject(res.data.data.project);
            setUser(res.data.data.user);
            setAuthenticated(true);
            setPassword(pwd);
        } catch (err: any) {
            if (err.response?.status === 401) {
                setAuthenticated(false);
            } else {
                toast.error('Failed to load invoice');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const pwd = searchParams.get('password') || '';
        fetchInvoice(pwd);
    }, [invoiceId, searchParams]);

    const handleLogin = (e: React.FormEvent) => {
        e.preventDefault();
        fetchInvoice(passInput);
    };

    const handlePrint = () => {
        window.print();
    };

    const handleAcceptInvoice = async () => {
        try {
            setLoading(true);
            const res = await crmAPI.acceptPublicInvoice(invoiceId!, password);
            setInvoice(res.data.data.invoice);
            toast.success('Invoice accepted successfully!');
        } catch (err: any) {
            toast.error(err.response?.data?.error || 'Failed to accept invoice');
        } finally {
            setLoading(false);
        }
    };

    const handleRejectInvoice = async () => {
        if (!rejectReason.trim()) {
            toast.error("Please provide a reason for rejection.");
            return;
        }
        try {
            setLoading(true);
            const res = await crmAPI.rejectPublicInvoice(invoiceId!, rejectReason.trim(), password);
            setInvoice(res.data.data.invoice);
            setShowRejectModal(false);
            toast.success('Invoice rejected.');
        } catch (err: any) {
            toast.error(err.response?.data?.error || 'Failed to reject invoice');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
            </div>
        );
    }

    if (!authenticated) {
        return (
            <div className="min-h-screen bg-white md:bg-gray-50 flex items-center justify-center p-6">
                <div className="w-full max-w-md bg-white md:rounded-[3rem] md:shadow-2xl md:shadow-indigo-900/5 p-12 text-center">
                    <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-8">
                        <LockClosedIcon className="w-10 h-10 text-gray-400" />
                    </div>
                    <h1 className="text-3xl font-black text-gray-900 mb-2 tracking-tight">Protected Invoice</h1>
                    <p className="text-sm font-medium text-gray-500 mb-8">Please enter the password provided by your photographer to view this invoice.</p>
                    
                    <form onSubmit={handleLogin} className="space-y-4">
                        <input
                            type="password"
                            value={passInput}
                            onChange={(e) => setPassInput(e.target.value)}
                            placeholder="Enter password"
                            className="w-full bg-gray-50 border-none rounded-2xl p-5 text-center text-lg font-bold tracking-widest focus:ring-2 focus:ring-teal-500 transition-all placeholder:text-gray-300 placeholder:font-medium placeholder:text-sm"
                            required
                        />
                        <button 
                            type="submit"
                            className="w-full py-5 bg-teal-500 hover:bg-teal-600 text-white rounded-2xl font-black uppercase tracking-widest text-sm transition-all shadow-lg shadow-teal-500/20"
                        >
                            Unlock Invoice
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    if (!invoice) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
                <div className="text-center">
                    <ExclamationCircleIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h1 className="text-2xl font-black text-gray-900 uppercase">Invoice Not Found</h1>
                    <p className="text-gray-500 mt-2">The invoice you are looking for does not exist or has been removed.</p>
                </div>
            </div>
        );
    }

    return (
        <>
        <style>{`
            @media print {
                @page { margin: 8mm 10mm; size: A4; }
                body { background: white !important; }
                .invoice-card { box-shadow: none !important; border-radius: 0 !important; }
                .invoice-header-row { display: flex !important; flex-direction: row !important; justify-content: space-between !important; align-items: flex-start !important; }
                .print-total-due {
                    background: #111827 !important;
                    -webkit-print-color-adjust: exact !important;
                    print-color-adjust: exact !important;
                    color: white !important;
                    border-radius: 1.25rem !important;
                    box-shadow: none !important;
                }
                .shadow-2xl, .shadow-xl, .shadow-lg, .shadow-md, .shadow-sm, .shadow {
                    box-shadow: none !important;
                }
                .print-total-due span:first-child { color: #9ca3af !important; }
                .print-total-due span:last-child { color: white !important; }
                .invoice-gstin-badge, .status-badge, .bg-white, .bg-gray-50, .border {
                    box-shadow: none !important;
                }
                .print-avoid-break {
                    page-break-inside: avoid !important;
                    break-inside: avoid !important;
                }

                /* Compact Layout for Single Page fitting values */
                .text-4xl { font-size: 1.5rem !important; }
                .text-2xl { font-size: 1.1rem !important; }
                .text-xl { font-size: 1rem !important; }
                .text-lg { font-size: 0.9rem !important; }
                .text-base { font-size: 0.85rem !important; }
                .text-sm { font-size: 10px !important; }
                .text-xs { font-size: 9px !important; }

                .p-12, .p-16, .md\:p-16, .p-6 {
                    padding: 0.4rem !important;
                }
                .py-8 {
                    padding-top: 0.25rem !important;
                    padding-bottom: 0.25rem !important;
                }
                .pb-6, .pb-8 {
                    padding-bottom: 0.15rem !important;
                }
                .mt-12 {
                    margin-top: 0.25rem !important;
                }
                .mt-8 {
                    margin-top: 0.25rem !important;
                }
                .mb-6 {
                    margin-bottom: 0.15rem !important;
                }
                .mb-8 {
                    margin-bottom: 0.25rem !important;
                }
                .gap-12 {
                    gap: 0.35rem !important;
                }
                .gap-8 {
                    gap: 0.25rem !important;
                }
                table td, table th {
                    padding-top: 0.25rem !important;
                    padding-bottom: 0.25rem !important;
                }
                /* Shrink images and headers for fit guarantee */
                img { max-height: 35px !important; }
                .w-16.h-16 { width: 35px !important; height: 35px !important; }
            }
        `}</style>
        <div className="min-h-screen bg-slate-50 font-sans text-gray-900 print:bg-white print:min-h-0 print:h-auto overflow-visible print:overflow-visible">
            {/* Nav / Controls (Hidden on Print) */}
            <div className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-100 px-6 py-4 print:hidden">
                <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-0">
                    <div className="flex items-center space-x-3 w-full sm:w-auto">
                        <BrandLogo iconSize="w-9 h-9" textSize="text-xl" />
                        <div className="h-5 w-px bg-gray-200"></div>
                        <h1 className="text-sm font-semibold uppercase tracking-widest text-gray-400 truncate">Invoice #{invoice.invoice_number}</h1>
                    </div>
                    <div className="flex items-center space-x-3 w-full sm:w-auto">
                        <button 
                            onClick={handlePrint}
                            className="w-full sm:w-auto flex items-center justify-center space-x-2 px-6 py-2.5 bg-gray-900 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-black transition-all shadow-lg shadow-black/10"
                        >
                            <PrinterIcon className="w-4 h-4" />
                            <span>Print / Save PDF</span>
                        </button>
                        {(invoice.status === 'draft' || invoice.status === 'sent') && (
                            <div className="flex w-full sm:w-auto space-x-2">
                                <button 
                                    onClick={() => setShowRejectModal(true)}
                                    className="flex-1 sm:flex-none flex items-center justify-center space-x-2 px-6 py-2.5 bg-white text-gray-700 border border-gray-200 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-gray-50 transition-all shadow-sm"
                                >
                                    <span>Reject</span>
                                </button>
                                <button 
                                    onClick={handleAcceptInvoice}
                                    className="flex-1 sm:flex-none flex items-center justify-center space-x-2 px-6 py-2.5 bg-teal-500 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-teal-600 transition-all shadow-lg shadow-teal-500/20"
                                >
                                    <CheckBadgeIcon className="w-4 h-4" />
                                    <span>Accept Invoice</span>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Status Banner */}
            {invoice.status === 'accepted' && (
                <div className="bg-teal-50 border-b border-teal-100 px-6 py-4 print:hidden">
                    <div className="max-w-4xl mx-auto flex items-center justify-center space-x-2 text-teal-700">
                        <CheckBadgeIcon className="w-5 h-5" />
                        <span className="text-sm font-bold">This invoice was accepted on {new Date(invoice.accepted_at).toLocaleString()}</span>
                    </div>
                </div>
            )}

            {/* Invoice Content */}
            <div className="max-w-4xl mx-auto px-6 py-12 print:px-0 print:py-0 print:m-0 print:max-w-full">
                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    className="bg-white rounded-[2rem] md:rounded-[3rem] shadow-2xl shadow-indigo-900/5 ring-1 ring-gray-100 overflow-hidden print:rounded-none print:shadow-none print:ring-0"
                >
                    {/* Branding Header — Moved here to prevent extra page on print */}
                    <div className="bg-gray-50/50 border-b border-gray-100 px-6 py-3 flex items-center justify-center space-x-3 print:bg-white">
                        <p className="text-[8px] font-black uppercase tracking-widest text-gray-400">Authenticated By</p>
                        <BrandLogo iconSize="w-5 h-5" textSize="text-xs" />
                        <div className="h-3 w-px bg-gray-200" />
                        <span className="text-[8px] font-black text-gray-400 uppercase tracking-widest">Official CRM</span>
                    </div>

                    {/* Invoice Header */}
                    <div className="p-12 md:p-16 border-b border-gray-50 bg-gradient-to-br from-white to-gray-50">
                        <div className="invoice-header-row flex flex-col md:flex-row justify-between gap-12">
                            <div>
                                <h2 className="text-4xl font-black tracking-tight mb-4 uppercase">Invoice</h2>
                                <div className="space-y-1">
                                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-400">Invoice Number</p>
                                    <p className="font-mono font-black text-xl">#{invoice.invoice_number}</p>
                                </div>
                                <div className="mt-8 grid grid-cols-2 gap-8">
                                    <div className="space-y-1">
                                        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-400">Issue Date</p>
                                        <p className="font-bold text-sm tracking-tight">{new Date(invoice.created_at).toLocaleDateString()}</p>
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-400">Due Date</p>
                                        <p className="font-bold text-sm tracking-tight">{new Date(invoice.due_date).toLocaleDateString()}</p>
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-400">Bill To</p>
                                        <p className="font-bold text-sm tracking-tight">{project?.client_name}</p>
                                    </div>
                                </div>
                            </div>

                            <div className="text-right">
                                <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mb-4 ml-auto overflow-hidden border border-gray-100 shadow-sm">
                                  {user?.business_logo && !logoError ? (
                                    <img 
                                      src={user.business_logo_signed || resolveImageUrl(user.business_logo)} 
                                      alt="Business Logo" 
                                      className="w-full h-full object-contain p-1" 
                                      onError={() => setLogoError(true)}
                                    />
                                  ) : user?.profile_pic ? (
                                    <img src={`${API_BASE_URL}/${user.profile_pic}`} alt="Logo" className="w-full h-full object-cover" />
                                  ) : (
                                    <span className="text-gray-900 font-black text-2xl">{user?.company_name?.charAt(0) || user?.first_name?.charAt(0)}</span>
                                  )}
                                </div>
                                {user?.company_name ? (
                                  <h2 className="text-xl font-black text-gray-900">{user.company_name}</h2>
                                ) : (
                                  <h2 className="text-xl font-black text-gray-900">{user?.first_name} {user?.last_name}</h2>
                                )}
                                <p className="text-sm text-gray-500 font-medium">{user?.email}</p>
                                {user?.address && (
                                  <p className="text-xs text-gray-400 font-medium mt-1 mb-4 whitespace-pre-wrap">{user.address}</p>
                                )}
                                {!user?.address && <div className="mb-4"></div>}
                                
                                {user?.gst_number && invoice?.tax > 0 && (
                                  <div className="mt-2 inline-flex items-center space-x-2 bg-teal-50 px-3 py-1 rounded-full mb-6">
                                    <span className="text-[8px] font-black uppercase tracking-widest text-teal-600">GSTIN</span>
                                    <span className="text-[10px] font-black text-teal-700">{user.gst_number}</span>
                                  </div>
                                )}
                                
                                <div className="clear-both"></div>
                                
                                <div className="inline-block p-4 bg-white rounded-2xl shadow-sm border border-gray-100 mb-6 transition-transform hover:scale-105 duration-300">
                                    <span className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-sm ring-1 ring-inset ${
                                        invoice.status === 'paid' 
                                        ? 'bg-emerald-50 text-emerald-600 ring-emerald-600/20' 
                                        : 'bg-amber-50 text-amber-600 ring-amber-600/20'
                                    }`}>
                                        Status: {invoice.status}
                                    </span>
                                </div>
                                {/* <div className="space-y-1">
                                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-400">Amount Due</p>
                                    <p className="text-4xl font-black tracking-tighter text-indigo-600">₹{invoice.total_amount}</p>
                                </div> */}
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="p-12 md:p-16">
                        <table className="w-full text-left">
                            <thead>
                                <tr className="border-b border-gray-100">
                                    <th className="pb-6 text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">Description</th>
                                    <th className="pb-6 text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 text-center">Qty</th>
                                    <th className="pb-6 text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 text-right">Unit Price (₹)</th>
                                    <th className="pb-6 text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 text-right">Amount (₹)</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {invoice.items?.map((item: any, i: number) => (
                                    <tr key={i} className="group transition-colors hover:bg-gray-50/50 print-avoid-break">
                                        <td className="py-8 font-bold text-sm tracking-tight">{item.description}</td>
                                        <td className="py-8 text-center font-mono font-bold text-sm text-gray-400">{item.quantity}</td>
                                        <td className="py-8 text-right font-mono font-bold text-sm text-gray-400">{item.unit_price}</td>
                                        <td className="py-8 text-right font-mono font-black text-sm">{item.total}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Totals */}
                        <div className="mt-12 ml-auto w-full max-w-xs space-y-4 print-avoid-break">
                            <div className="flex justify-between items-center px-4">
                                <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">Subtotal</span>
                                <span className="font-mono font-bold text-sm">₹{invoice.subtotal}</span>
                            </div>
                            {(invoice.discount_amount > 0) && (
                                <div className="flex justify-between items-center px-4">
                                    <span className="text-[10px] font-black uppercase tracking-widest text-emerald-500">Discount</span>
                                    <span className="font-mono font-bold text-sm text-emerald-500">-₹{invoice.discount_amount}</span>
                                </div>
                            )}
                            {(invoice.tax > 0) && (
                                <div className="flex justify-between items-center px-4">
                                    <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">Tax</span>
                                    <span className="font-mono font-bold text-sm">₹{invoice.tax}</span>
                                </div>
                            )}
                            <div className="flex justify-between items-center p-6 print-total-due bg-gradient-to-br from-gray-900 to-black rounded-[1.5rem] text-white shadow-xl shadow-gray-900/20">
                                <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">Total Due</span>
                                <span className="text-2xl font-black">₹{invoice.total_amount}</span>
                            </div>
                        </div>
                    </div>

                    {/* Notes Footer */}
                    {invoice.notes && (
                        <div className="p-12 md:p-16 bg-gray-50 border-t border-gray-100 print-avoid-break">
                            <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 mb-4">Account Notes</h4>
                            <p className="text-sm font-medium text-gray-600 leading-relaxed max-w-2xl">
                                {invoice.notes}
                            </p>
                        </div>
                    )}

                    {/* Signature / Acceptance Section */}
                    <div className="p-12 md:p-16 border-t border-gray-100 bg-gray-50/50 print-avoid-break">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            {/* Client Acceptance */}
                            <div>
                                <h4 className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-4">Client Acceptance</h4>
                                {invoice.status === 'rejected' ? (
                                    <div className="flex flex-col items-center">
                                        <div className="w-full max-w-[220px] border-b border-red-300 pb-1 text-center">
                                            <p className="font-serif text-xl italic text-red-700">{project?.client_name}</p>
                                        </div>
                                        <p className="text-[9px] font-black uppercase tracking-widest text-red-400 mt-2">Client</p>
                                        <p className="text-[8px] font-medium text-red-400 mt-0.5">Rejected</p>
                                    </div>
                                ) : invoice.accepted_at ? (
                                    <div className="flex flex-col items-center">
                                        <div className="w-full max-w-[220px] border-b border-teal-200 pb-1 text-center">
                                            <p className="font-serif text-xl italic text-gray-900">{project?.client_name}</p>
                                        </div>
                                        <p className="text-[9px] font-black uppercase tracking-widest text-gray-400 mt-2">Client</p>
                                        <p className="text-[8px] font-medium text-gray-400 mt-0.5">Accepted on {new Date(invoice.accepted_at).toLocaleDateString()}</p>
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center">
                                        <div className="w-full max-w-[220px] border-b border-dashed border-gray-200 pb-1 text-center">
                                            <p className="text-xs text-gray-300 font-medium pt-3">Draw signature here</p>
                                        </div>
                                        <p className="text-[9px] font-black uppercase tracking-widest text-gray-400 mt-2">Client</p>
                                        <p className="text-[8px] font-medium text-gray-400 mt-0.5">Awaiting acceptance</p>
                                    </div>
                                )}
                            </div>
                            {/* Photographer / Service Provider */}
                            <div>
                                <h4 className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-4">Issued By</h4>
                                <div className="flex flex-col items-center">
                                    <div className="w-full max-w-[220px] border-b border-gray-300 pb-1 text-center">
                                        <p className="font-serif text-xl italic text-gray-900">
                                            {user?.company_name || `${user?.first_name} ${user?.last_name}`}
                                        </p>
                                    </div>
                                    <p className="text-[9px] font-black uppercase tracking-widest text-gray-400 mt-2">Service Provider</p>
                                    {user?.email && (
                                        <p className="text-[8px] font-medium text-gray-400 mt-0.5">{user.email}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>

            </div>

            {/* Reject Modal */}
            {showRejectModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-sm print:hidden">
                    <motion.div 
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-white rounded-3xl p-8 max-w-sm w-full shadow-2xl border border-gray-100"
                    >
                        <h3 className="text-xl font-black text-gray-900 mb-2">Reject Invoice</h3>
                        <p className="text-sm text-gray-500 mb-6">Please let the photographer know why you are rejecting this invoice.</p>
                        
                        <textarea
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            className="w-full bg-gray-50 border-none rounded-2xl p-4 text-sm font-medium focus:ring-2 focus:ring-teal-500 transition-all placeholder:text-gray-400 min-h-[120px] mb-6 resize-none"
                            placeholder="Reason for rejection..."
                        />
                        
                        <div className="flex space-x-3">
                            <button
                                onClick={() => setShowRejectModal(false)}
                                className="flex-1 py-3 px-4 bg-gray-100 text-gray-700 rounded-xl font-bold text-sm tracking-wide hover:bg-gray-200 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleRejectInvoice}
                                className="flex-1 py-3 px-4 bg-red-500 text-white rounded-xl font-bold text-sm tracking-wide hover:bg-red-600 transition-colors shadow-lg shadow-red-500/20"
                            >
                                Reject
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </div>
        </>
    );
};

export default InvoicePage;
